import { Navigate, Route, Routes } from 'react-router-dom';

import { mapObject } from '@client/toolkit';

import { BuildingListRoute } from './buildings/building-list/building-list.route';
import {
  BuildingInvestmentsRoute,
  BuildingCreateRoute,
  BuildingRoute,
  BuildingDashboardRoute,
  BuildingArealerRoute,
  BuildingAssociationsRoute,
  BuildingEditRoute,
  BuildingAgreementPartiesRoute,
  BuildingAgreementsRoute,
  BuildingCompaniesOwnersRoute,
  BuildingCompaniesManagersRoute,
  BuildingCompaniesRolesRoute,
  BuildingEnergyRoute,
  BuildingPortfolioTabRoute,
  BuildingFinancingTabRoute,
  AgreementImportWizardRoute,
} from './buildings/building/index';

import { PortfolioImportRoute } from './portfolio-import/portfolio-import.route';
import { SpaceHierarchyRoute } from './space-hierarchy/space-hierarchy.route';
import { SpaceDashboardRoute } from './spaces/space/dashboard/space-dashboard.route';
import {
  SpaceAgreementsRoute,
  SpaceAgreementPartiesRoute,
  SpaceInvestmentsRoute,
  SpaceEditRoute,
  SpaceRoute,
  SpaceRedirectRoute,
  SpaceCreateRoute,
  SpaceCompaniesOwnersRoute,
  SpaceCompaniesManagersRoute,
  SpaceCompaniesRolesRoute,
  SpaceArealerRoute,
  SpaceAssociationsRoute,
} from './spaces/space';
import { PermissionKeys } from '@client/api';
import { PermissionProtectedRoute } from '../../components/permission-protected-route';

export const PortfolioPaths = {
  Home: '/portfolio',

  Onboarding: '/onboarding/portfolio',

  PortfolioImport: '/portfolio/buildings/import',

  BuildingList: '/portfolio/buildings',
  BuildingCreate: '/portfolio/buildings/create',
  Building: '/portfolio/buildings/:buildingId',
  BuildingDashboard: '/portfolio/buildings/:buildingId',
  BuildingArealer: '/portfolio/buildings/:buildingId/arealer',
  BuildingAssociations: '/portfolio/buildings/:buildingId/associations',
  BuildingAgreements: '/portfolio/buildings/:buildingId/agreements',
  BuildingAgreementsImport: '/portfolio/buildings/:buildingId/agreements/import',
  BuildingAgreementParties: '/portfolio/buildings/:buildingId/agreement-parties',
  BuildingInvestments: '/portfolio/buildings/:buildingId/investments',
  BuildingOwners: '/portfolio/buildings/:buildingId/owners',
  BuildingManagers: '/portfolio/buildings/:buildingId/managers',
  BuildingRoles: '/portfolio/buildings/:buildingId/roles',
  BuildingEnergy: '/portfolio/buildings/:buildingId/energy',
  BuildingPortfolio: '/portfolio/buildings/:buildingId/economy',
  BuildingFinancing: '/portfolio/buildings/:buildingId/financing',
  BuildingEdit: '/portfolio/buildings/:buildingId/edit',
  BuildingChildCreate: '/portfolio/buildings/:buildingId/spaces/create/:createDiscriminator',

  UnspecificSpace: '/portfolio/buildings/:buildingId/spaces/:spaceId', // deprecated shouldn't be used anymore!

  Space: '/portfolio/buildings/:buildingId/spaces/:discriminator/:spaceId',
  SpaceDashboard: '/portfolio/buildings/:buildingId/spaces/:discriminator/:spaceId',
  SpaceArealer: '/portfolio/buildings/:buildingId/spaces/:discriminator/:spaceId/arealer',
  SpaceAssociations: '/portfolio/buildings/:buildingId/spaces/:discriminator/:spaceId/associations',
  SpaceAgreements: '/portfolio/buildings/:buildingId/spaces/:discriminator/:spaceId/agreements',
  SpaceAgreementParties: '/portfolio/buildings/:buildingId/spaces/:discriminator/:spaceId/agreement-parties',
  SpaceInvestments: '/portfolio/buildings/:buildingId/spaces/:discriminator/:spaceId/investments',
  SpaceOwners: '/portfolio/buildings/:buildingId/spaces/:discriminator/:spaceId/owners',
  SpaceManagers: '/portfolio/buildings/:buildingId/spaces/:discriminator/:spaceId/managers',
  SpaceRoles: '/portfolio/buildings/:buildingId/spaces/:discriminator/:spaceId/roles',
  SpaceEdit: '/portfolio/buildings/:buildingId/spaces/:discriminator/:spaceId/edit',
  SpaceChildCreate: '/portfolio/buildings/:buildingId/spaces/:discriminator/:spaceId/create/:createDiscriminator',
};

const RouterPaths = mapObject(PortfolioPaths, (val: string) => val.replace(PortfolioPaths.Home, ''));

export function PortfolioRouter() {
  return (
    <Routes>
      <Route
        path={RouterPaths.BuildingList}
        element={
          <PermissionProtectedRoute requiredPermission={[PermissionKeys.Tenant.SpacesList]}>
            <BuildingListRoute />
          </PermissionProtectedRoute>
        }
      >
        <Route path={RouterPaths.PortfolioImport} element={<PortfolioImportRoute />} />
        <Route path={RouterPaths.BuildingCreate} element={<BuildingCreateRoute />} />
      </Route>

      {/* This route is protected in the SpaceHierarchyRoute component because we need buildingId to check for permission */}
      <Route path={RouterPaths.Building} element={<SpaceHierarchyRoute />}>
        <Route path={RouterPaths.Building} element={<BuildingRoute />}>
          <Route index element={<BuildingDashboardRoute />} />

          <Route element={<BuildingDashboardRoute />}>
            <Route path={RouterPaths.BuildingChildCreate} element={<SpaceCreateRoute />} />
          </Route>

          <Route path={RouterPaths.BuildingEdit} element={<BuildingEditRoute />} />
          <Route path={RouterPaths.BuildingArealer} element={<BuildingArealerRoute />} />
          <Route path={RouterPaths.BuildingAssociations} element={<BuildingAssociationsRoute />} />
          <Route path={RouterPaths.BuildingAgreements} element={<BuildingAgreementsRoute />}>
            <Route path={RouterPaths.BuildingAgreementsImport} element={<AgreementImportWizardRoute />} />
          </Route>
          <Route path={RouterPaths.BuildingAgreementParties} element={<BuildingAgreementPartiesRoute />} />
          <Route path={RouterPaths.BuildingPortfolio} element={<BuildingPortfolioTabRoute />} />
          <Route path={RouterPaths.BuildingFinancing} element={<BuildingFinancingTabRoute />} />
          <Route path={RouterPaths.BuildingRoles} element={<BuildingCompaniesRolesRoute />} />
          <Route path={RouterPaths.BuildingOwners} element={<BuildingCompaniesOwnersRoute />} />
          <Route path={RouterPaths.BuildingManagers} element={<BuildingCompaniesManagersRoute />} />
          <Route path={RouterPaths.BuildingInvestments} element={<BuildingInvestmentsRoute />} />
          <Route path={RouterPaths.BuildingEnergy} element={<BuildingEnergyRoute />} />
        </Route>

        <Route path={RouterPaths.UnspecificSpace} element={<SpaceRedirectRoute />} />

        <Route path={RouterPaths.Space} element={<SpaceRoute />}>
          <Route index element={<SpaceDashboardRoute />} />

          <Route element={<SpaceDashboardRoute />}>
            <Route path={RouterPaths.SpaceEdit} element={<SpaceEditRoute />} />
            <Route path={RouterPaths.SpaceChildCreate} element={<SpaceCreateRoute />} />
          </Route>

          <Route path={RouterPaths.SpaceArealer} element={<SpaceArealerRoute />} />
          <Route path={RouterPaths.SpaceAssociations} element={<SpaceAssociationsRoute />} />
          <Route path={RouterPaths.SpaceAgreements} element={<SpaceAgreementsRoute />} />
          <Route path={RouterPaths.SpaceAgreementParties} element={<SpaceAgreementPartiesRoute />} />
          <Route path={RouterPaths.SpaceInvestments} element={<SpaceInvestmentsRoute />} />
          <Route path={RouterPaths.SpaceRoles} element={<SpaceCompaniesRolesRoute />} />
          <Route path={RouterPaths.SpaceOwners} element={<SpaceCompaniesOwnersRoute />} />
          <Route path={RouterPaths.SpaceManagers} element={<SpaceCompaniesManagersRoute />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to={PortfolioPaths.BuildingList} replace />} />
    </Routes>
  );
}
